import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from './Button';
import { ROLE_TONE } from '@/data/categories';
import { postTimestamp, replyCountLabel } from '@/lib/format';
import type { Question } from '@/types';
import styles from '@/sections/Qa.module.css';

export interface QuestionThreadProps {
  readonly question: Question;
  readonly onReply: (questionId: string, body: string) => void;
}

export function QuestionThread({ question, onReply }: QuestionThreadProps) {
  const [draft, setDraft] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    onReply(question.id, body);
    setDraft('');
  };

  const roleClass = ROLE_TONE[question.role] === 'accent' ? styles.roleAccent : styles.roleBrand;

  return (
    <article className={styles.question}>
      <div className={styles.meta}>
        <span className={`${styles.role} ${roleClass}`}>
          <span className={styles.dot} aria-hidden="true" />
          {question.role}
        </span>
        <span className={styles.author}>{question.author}</span>
        <span className={styles.when}>{postTimestamp(question)}</span>
        <span className={styles.count}>{replyCountLabel(question.replies.length)}</span>
      </div>

      <h3 className={styles.body}>{question.body}</h3>

      {question.replies.map((reply) => (
        <div key={reply.id} className={styles.reply}>
          <div className={styles.replyMeta}>
            <span className={styles.replyAuthor}>{reply.author}</span>
            <span className={styles.replyWhen}>
              {reply.role} · {postTimestamp(reply)}
            </span>
          </div>
          <p className={styles.replyBody}>{reply.body}</p>
        </div>
      ))}

      <form className={styles.replyForm} onSubmit={submit}>
        <input
          type="text"
          className={styles.replyInput}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="اكتب ردًا…"
          aria-label={`اكتب ردًا على سؤال ${question.author}`}
        />
        <Button variant="quiet" type="submit" disabled={!draft.trim()}>
          أرسل الرد
        </Button>
      </form>
    </article>
  );
}
